import { formatDate } from "@omnabase/ui";
import type { Customer, SiteFacts } from "@omnabase/shared";
import type { Facts } from "./integrations";

/** The customer sources panel round (2026-11-04, brief `docs/briefs/customer-sources-panel-round.md` parts one to three and its decisions; red
 *  team findings 1 to 9, 13, 14 and 17): what the ticket sidebar's Profile tab and the customer page draw about a customer's sources, from two
 *  reads: the lookup (what the ticket's project's source answered just now) and the stored licence rows. Pure: no React, no network.
 *
 *  In plain words:
 *  - One section per source. The live one (the source the lookup asked) holds its card fields, its link fields as buttons, its licences, the
 *    answer's subscriptions, orders and account. Every other source that has stored licences gets a licences-only section, in the workspace's
 *    project order; the licences the customer claimed come last, "Claimed by the customer".
 *  - A section's licences are the stored rows of that source, the same rows the Information row "Licenses" counts, so the two never disagree; a
 *    stored row borrows its shop link from the answer that just came in (same product and key).
 *  - The digest (the folded line): the first two card fields that are not links, then the licence count; without card fields the counts;
 *    "Nothing known" when a source answered with nothing at all.
 *  - Values read as words: statuses capitalised, dates the app's way ("Until May 1, 2027"), websites one per entry. */
type Licence = Customer["licenses"][number];
type Fact = NonNullable<Facts["facts"]>[number];

export type SectionLicence = { product: string; key: string | null; status: string | null; until: string | null; version: string | null; sites: string[]; href?: string };
export type SectionSubscription = { product: string; status: string | null; renews: string | null; href?: string };
export type SectionOrder = { number: string; date: string | null; total: string | null; status: string | null; href?: string };
export type SourceSection = {
  /** "endpoint:<id>", "site:<site id>" or "claimed" */ key: string; title: string; /** the host, small beside a title that is a name */ host: string | null;
  digest: string; /** the id part of the Licences group's anchor ("licences-<anchor>") */ anchor: string; /** the source the lookup asked just now */ live: boolean;
  fields: { label: string; value: string }[]; links: { label: string; href: string }[];
  licences: SectionLicence[]; subscriptions: SectionSubscription[]; orders: SectionOrder[]; account: { registered: string | null; roles: string | null } | null;
};
export type SectionsInput = { facts: Facts | null | undefined; licences: Licence[]; /** the workspace's project ids in their order */ projects: string[]; ticketProjectId?: string | null };

export const CLAIMS_KEY = "claimed";
export const CLAIMS_TITLE = "Claimed by the customer";

/** the sections in order: the lookup's own source, then the other sources with stored licences, then the claims */
export function sourceSections({ facts, licences, projects, ticketProjectId }: SectionsInput): SourceSection[] {
  const live = liveSection(facts, licences);
  const rest = licences.filter((l) => keyOf(l) !== live?.key && keyOf(l) !== CLAIMS_KEY);
  const claims = licences.filter((l) => keyOf(l) === CLAIMS_KEY);
  return [...(live ? [live] : []), ...storedSections(rest, projects, ticketProjectId ?? null), ...(claims.length ? [claimsSection(claims)] : [])];
}

/** the rows the screen draws: the lookup's own copy (read right after its write, finding 1) when it carries one, else the customer read's */
export const licencesOf = (customer: { licenses: Licence[] } | null | undefined, facts: Facts | null | undefined): Licence[] => facts?.licences ?? customer?.licenses ?? [];

/** the Information row "Licenses": "…" only while a lookup runs and nothing is stored, "None" after it, else the count and whether one is verified */
export function licencesRowValue(rows: Licence[], lookingUp: boolean): string {
  if (!rows.length) return lookingUp ? "…" : "None";
  return `${rows.length} · ${rows.some((l) => l.verified) ? "verified" : "claimed, not verified"}`;
}

/** a link field's button words: the answer's words, else "Open in <host>" when the answer is only an address; null for a field that is not an https link */
export function linkFieldLabel(f: { value: string; href: string | null }): string | null {
  const href = f.href ?? (httpsHost(f.value) ? f.value : null);
  const host = href ? httpsHost(href) : null;
  if (!href || !host) return null;
  return httpsHost(f.value) ? `Open in ${httpsHost(f.value)}` : f.value;
}

/* ---------- the sections ---------- */

const keyOf = (l: Licence): string => l.sourceKey ?? (l.source === "endpoint" || l.source === "site" ? `${l.source}:?` : CLAIMS_KEY);
const anchorOf = (key: string): string => key.replace(/[^a-z0-9]+/gi, "-");

function liveSection(facts: Facts | null | undefined, licences: Licence[]): SourceSection | null {
  const e = facts?.endpoint;
  if (!facts || !e) return null;
  const key = e.key ?? `${e.site ? "site" : "endpoint"}:${e.id}`;
  const rows = licences.filter((l) => keyOf(l) === key);
  if (!facts.facts && !rows.length) return null; // a lookup that failed with nothing stored draws nothing
  const block = facts.omnabase ?? null;
  const shown = (facts.facts ?? []).map((f) => ({ f, link: linkFieldLabel(f) }));
  const body = {
    fields: shown.filter((x) => !x.link).map(({ f }) => ({ label: f.label, value: fieldWords(f.value) })),
    links: shown.filter((x) => x.link).map(({ f, link }) => ({ label: link!, href: (f.href ?? f.value) })),
    licences: rows.map((l) => licenceOf(l, block)), subscriptions: (block?.subscriptions ?? []).map(subscriptionOf), orders: (block?.orders ?? []).map(orderOf), account: accountOf(block),
  };
  const named = !!e.name && !e.site;
  return { key, title: e.name || e.host, host: named ? e.host : null, anchor: anchorOf(key), live: true, ...body, digest: digestOf(body, !!facts.facts) };
}

/** the other sources' stored licences, one section each, the ticket's project's first, then by the workspace's project order */
function storedSections(rows: Licence[], projects: string[], ticketProjectId: string | null): SourceSection[] {
  const groups = new Map<string, Licence[]>();
  for (const l of rows) groups.set(keyOf(l), [...(groups.get(keyOf(l)) ?? []), l]);
  const rank = (ls: Licence[]) => Math.min(...ls.map((l) => (l.projectId === ticketProjectId ? -1 : projects.includes(l.projectId) ? projects.indexOf(l.projectId) : projects.length)));
  return [...groups.entries()].sort(([, a], [, b]) => rank(a) - rank(b)).map(([key, ls]) => {
    const first = ls[0]!, title = first.sourceName ?? first.siteHost ?? "Customer data";
    const host = first.source === "endpoint" && first.siteHost && first.siteHost !== title ? first.siteHost : null;
    return licencesOnly(key, title, host, ls);
  });
}
const claimsSection = (rows: Licence[]): SourceSection => licencesOnly(CLAIMS_KEY, CLAIMS_TITLE, null, rows);
function licencesOnly(key: string, title: string, host: string | null, rows: Licence[]): SourceSection {
  const body = { fields: [], links: [], licences: rows.map((l) => licenceOf(l, null)), subscriptions: [], orders: [], account: null };
  return { key, title, host, anchor: anchorOf(key), live: false, ...body, digest: digestOf(body, false) };
}

type Body = Pick<SourceSection, "fields" | "links" | "licences" | "subscriptions" | "orders" | "account">;
/** the folded line (Q1 as refined, finding 6) */
function digestOf(b: Body, answered: boolean): string {
  const licences = count(b.licences.length, "licence", "licences");
  if (b.fields.length) return [...b.fields.slice(0, 2).map((f) => f.value), ...(licences ? [licences] : [])].join(" · ");
  const counts = [licences, count(b.subscriptions.length, "subscription", "subscriptions"), count(b.orders.length, "order", "orders")].filter(Boolean);
  if (counts.length) return counts.join(" · ");
  if (b.links.length) return b.links[0]!.label;
  return b.account ? "Account" : answered ? "Nothing known" : "";
}
const count = (n: number, one: string, many: string): string | null => (n ? `${n} ${n === 1 ? one : many}` : null);

/* ---------- the rows as words ---------- */

function licenceOf(l: Licence, block: SiteFacts | null): SectionLicence {
  const claim = !l.verified && (l.source === "claimed" || keyOf(l) === CLAIMS_KEY);
  const href = block?.licences.find((x) => x.product === l.product && (x.keyMasked ?? null) === l.keyMasked)?.href; // finding 4: borrowed from the answer just in
  return { product: l.product, key: l.keyMasked, status: claim ? "Not verified" : statusWord(l.status), until: l.renewsAt ? `Until ${dayWords(l.renewsAt)}` : null, version: l.version ?? null, sites: l.sites.map((s) => s.host), ...(href ? { href } : {}) };
}
const subscriptionOf = (x: SiteFacts["subscriptions"][number]): SectionSubscription => ({ product: x.product, status: statusWord(x.status), renews: x.renewsAt ? `Renews ${dayWords(x.renewsAt)}` : null, ...(x.href ? { href: x.href } : {}) });
const orderOf = (o: SiteFacts["orders"][number]): SectionOrder => ({ number: o.number, date: o.date ? dayWords(o.date) : null, total: o.total ?? null, status: statusWord(o.status), ...(o.href ? { href: o.href } : {}) });
function accountOf(block: SiteFacts | null): SourceSection["account"] {
  const p = block?.profile;
  if (!p) return null;
  return { registered: p.registeredAt ? dayWords(p.registeredAt) : null, roles: p.roles?.length ? capitalised(p.roles.join(", ")) : null };
}

/** "active" reads "Active", "on_hold" "On hold"; nothing stays nothing */
export function statusWord(s: string | null | undefined): string | null {
  const words = (s ?? "").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words ? capitalised(words) : null;
}
const capitalised = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_LIKE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const UTC_DAY = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
/** a day the app's way ("May 1, 2027"); a date without a time (or one stored as midnight UTC) is that day everywhere, so it is read in UTC
 *  (finding 14: a screen west of UTC would show the day before); words that are no date stay as they are */
export function dayWords(value: string): string {
  const d = new Date(value);
  if (!DATE_LIKE.test(value.trim()) || Number.isNaN(d.getTime())) return value;
  return DATE_ONLY.test(value.trim()) || d.toISOString().endsWith("T00:00:00.000Z") ? UTC_DAY.format(d) : formatDate(value);
}
/** a card field's value: a whole date reads as a day, anything else as sent (the AI still gets the text as sent) */
const fieldWords = (value: Fact["value"]): string => (DATE_LIKE.test(value.trim()) ? dayWords(value) : value);
const httpsHost = (value: string): string | null => { try { const u = new URL(value.trim()); return u.protocol === "https:" && u.hostname ? u.hostname : null; } catch { return null; } };

export { readSourceOpen, sourceOpenKey, writeSourceOpen, type SourcePlace } from "./sourceFolds";
